import React from 'react';
import ProfileDropdown from './ProfileDropdown';
import { FormProfile } from '../../services/profileService';

interface ProfileControlsBarProps {
  profiles: FormProfile[];
  selectedProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onSaveProfile: () => void;
  isLoading?: boolean;
  formName?: string;
}

/**
 * Profile controls bar component
 * Shows dropdown to load profiles and button to save current form state
 * Responsive: horizontal on desktop, stacked on mobile
 */
const ProfileControlsBar: React.FC<ProfileControlsBarProps> = ({
  profiles,
  selectedProfileId,
  onSelectProfile,
  onSaveProfile,
  isLoading = false,
  formName
}) => {
  return (
    <div className="mb-6 pb-4 border-b border-gray-200">
      {/* Desktop layout: horizontal */}
      <div className="hidden sm:flex items-center justify-between gap-4">
        {/* Left side: Load profile dropdown */}
        <div className="flex items-center gap-3">
          <ProfileDropdown
            profiles={profiles}
            selectedProfileId={selectedProfileId}
            onSelectProfile={onSelectProfile}
            isLoading={isLoading}
          />
          {profiles.length > 0 && (
            <p className="text-xs text-gray-500">
              {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'} saved
            </p>
          )}
        </div>

        {/* Right side: Save profile button */}
        <button
          type="button"
          onClick={onSaveProfile}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1d3a8b] hover:bg-[#152d6f] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d3a8b] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Save current form as profile"
        >
          <span className="material-icons" style={{ fontSize: '18px' }}>
            save
          </span>
          <span className="whitespace-nowrap">Save Profile</span>
        </button>
      </div>

      {/* Mobile layout: stacked vertically */}
      <div className="sm:hidden space-y-3">
        {/* Load profile dropdown (full width) */}
        <div className="w-full">
          <ProfileDropdown
            profiles={profiles}
            selectedProfileId={selectedProfileId}
            onSelectProfile={onSelectProfile}
            isLoading={isLoading}
          />
          {profiles.length > 0 && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'} saved
            </p>
          )}
        </div>

        {/* Save profile button (full width) */}
        <button
          type="button"
          onClick={onSaveProfile}
          disabled={isLoading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1d3a8b] hover:bg-[#152d6f] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d3a8b] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="Save current form as profile"
        >
          <span className="material-icons" style={{ fontSize: '18px' }}>
            save
          </span>
          <span className="whitespace-nowrap">Save Profile</span>
        </button>
      </div>

      {/* Empty state when no profiles exist */}
      {profiles.length === 0 && !isLoading && (
        <p className="text-xs text-gray-500 text-center mt-3">
          Save your first profile to quickly reuse form data later
        </p>
      )}
    </div>
  );
};

export default ProfileControlsBar;
